import { Link } from "react-router-dom";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { Star, ArrowRight, ShieldCheck, HandHeart, CheckCircle2, BookOpen, Video, Images } from "lucide-react";
import { SEO } from "@/components/SEO";
import { Reveal } from "@/components/Reveal";
import { GoogleReviews } from "@/components/GoogleReviews";
import { VideoTestimonials } from "@/components/VideoTestimonials";
import {
  BookButton, CallButton, SectionHeading, FinalCTA,
} from "@/components/shared";
import {
  business, images, conditions, treatments, whyChoose, process, faqs, routes,
} from "@/lib/site";
import { combine, localBusinessSchema, physicianSchema, organizationSchema, faqSchema } from "@/lib/schema";

const trustPoints = ["Personalized Care", "Non-Surgical Approach", "Patient-Focused Treatment"];

export default function Home() {
  const jsonLd = combine(localBusinessSchema, physicianSchema, organizationSchema, faqSchema(faqs));

  return (
    <>
      <SEO
        path={routes.home}
        title="Best Chiropractor in Varanasi | Dr. Satyam Jaiswal — Pain & Spine Care"
        description="Looking for the best chiropractor in Varanasi? Dr. Satyam Jaiswal offers personalized, non-surgical chiropractic care for back pain, neck pain, sciatica & spine concerns. Book an appointment today."
        jsonLd={jsonLd}
      />

      {/* HERO */}
      <section className="relative overflow-hidden bg-navy" data-testid="hero-section">
        <div className="absolute inset-0 hero-glow" />
        <div className="absolute inset-0 dotted-grid opacity-30" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-8 items-center pt-12 pb-8 lg:py-16">
            <div className="animate-fade-up">
              <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-xs font-semibold text-white/90 mb-6">
                <Star className="h-3.5 w-3.5 text-brand-orange fill-brand-orange" />
                Rated by 1,000+ patients across Varanasi
              </span>
              <h1 className="font-heading font-extrabold tracking-tight text-white text-4xl sm:text-5xl lg:text-[3.5rem] leading-[1.05]">
                Best Chiropractor in Varanasi for{" "}
                <span className="text-brand-orange">Pain Relief</span> &amp; Better Mobility
              </h1>
              <p className="mt-5 text-white/75 text-base sm:text-lg leading-relaxed max-w-xl">
                Personalized chiropractic care with {business.doctorName}. We help you address
                back pain, neck pain, spine and mobility concerns through a patient-focused,
                non-surgical approach.
              </p>

              <div className="mt-8 flex flex-wrap gap-3">
                <BookButton testId="hero-book-btn" />
                <CallButton testId="hero-call-btn" />
              </div>

              <div className="mt-9 flex flex-wrap gap-x-6 gap-y-3">
                {trustPoints.map((t) => (
                  <span key={t} className="flex items-center gap-2 text-sm font-medium text-white/85">
                    <CheckCircle2 className="h-4 w-4 text-brand-orange" /> {t}
                  </span>
                ))}
              </div>
            </div>

            <div className="relative flex justify-center lg:justify-end">
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="h-[80%] w-[80%] rounded-full bg-brand-orange/20 blur-3xl" />
              </div>
              <img
                src={images.doctorHero}
                alt="Dr. Satyam Jaiswal, Chiropractor and Pain Specialist in Varanasi"
                className="relative z-10 w-full max-w-md object-contain drop-shadow-2xl"
                loading="eager"
                width="480"
                data-testid="hero-doctor-image"
              />
            </div>
          </div>

          {/* stats strip */}
          <div className="relative grid grid-cols-3 gap-4 border-t border-white/10 py-6">
            {business.stats.map((s) => (
              <div key={s.label} className="text-center">
                <div className="font-heading font-extrabold text-2xl sm:text-3xl lg:text-4xl text-white">{s.value}</div>
                <div className="text-[11px] sm:text-sm text-white/60 mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INTRODUCTION */}
      <section className="bg-white py-20 lg:py-28" data-testid="intro-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <Reveal className="order-2 lg:order-1">
            <div className="relative">
              <img
                src={images.consult}
                alt="Dr. Satyam Jaiswal consulting with a patient in Varanasi"
                className="rounded-2xl w-full object-cover shadow-md aspect-[4/3]"
                loading="lazy"
              />
              <div className="absolute -bottom-6 -right-4 sm:right-6 bg-navy text-white rounded-2xl px-6 py-5 shadow-lg max-w-[220px]">
                <HandHeart className="h-7 w-7 text-brand-orange mb-2" />
                <p className="text-sm font-semibold leading-snug">Care built around understanding your concern</p>
              </div>
            </div>
          </Reveal>
          <Reveal delay={120} className="order-1 lg:order-2">
            <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-brand-orange mb-3">
              Meet Your Chiropractor
            </span>
            <h2 className="font-heading font-extrabold tracking-tight text-navy text-3xl sm:text-4xl leading-[1.1]">
              {business.doctorName}
            </h2>
            <p className="mt-2 font-semibold text-ink">{business.role}</p>
            <p className="mt-5 text-ink leading-relaxed">
              Dr. Satyam Jaiswal is a chiropractor and pain &amp; spine care professional in Varanasi.
              He focuses on personalized care for people experiencing back pain, neck pain,
              posture-related concerns, mobility issues and other musculoskeletal problems.
            </p>
            <p className="mt-4 text-ink leading-relaxed">
              His approach emphasizes understanding the underlying cause of your concern rather than
              focusing only on temporary relief — combining careful assessment, patient education and
              appropriate non-surgical care.
            </p>
            <Link
              to={routes.about}
              data-testid="intro-know-more"
              className="mt-7 inline-flex items-center gap-2 font-bold text-navy hover:text-brand-orange transition-colors group"
            >
              Know More About Dr. Satyam
              <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* CONDITIONS */}
      <section className="bg-soft py-20 lg:py-28" data-testid="conditions-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading
            center
            eyebrow="Conditions We Help With"
            title="Concerns We Commonly Care For"
            sub="From everyday stiffness to persistent pain, we provide assessment and personalized support for a range of musculoskeletal concerns."
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {conditions.map((c, i) => (
              <Reveal key={c.name} delay={i * 60}>
                <Link
                  to={routes.symptoms}
                  data-testid={`condition-${c.name.toLowerCase().replace(/\s/g, "-")}`}
                  className="group block h-full bg-white rounded-2xl p-6 shadow-sm hover:shadow-md hover:-translate-y-1 transition-[transform,box-shadow] duration-300 border border-transparent hover:border-brand-orange/20"
                >
                  <div className="grid h-12 w-12 place-items-center rounded-xl bg-navy/5 group-hover:bg-brand-orange/10 transition-colors">
                    <c.icon className="h-6 w-6 text-navy group-hover:text-brand-orange transition-colors" />
                  </div>
                  <h3 className="mt-4 font-heading font-bold text-navy text-lg">{c.name}</h3>
                  <p className="mt-1.5 text-sm text-ink leading-relaxed">{c.desc}</p>
                </Link>
              </Reveal>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to={routes.symptoms} data-testid="explore-symptoms" className="inline-flex items-center gap-2 font-bold text-navy hover:text-brand-orange transition-colors group">
              Explore Symptoms <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      {/* TREATMENTS */}
      <section className="bg-white py-20 lg:py-28" data-testid="treatments-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="Chiropractic Treatments"
            title="A Personalized, Non-Surgical Care Approach"
            sub="Every plan is tailored to your body and your goals — focused on comfort, movement and long-term function."
          />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {treatments.map((t, i) => (
              <Reveal key={t.name} delay={i * 60}>
                <div className="h-full bg-soft rounded-2xl p-8 hover:shadow-md transition-shadow duration-300">
                  <div className="grid h-12 w-12 place-items-center rounded-xl bg-brand-orange/10">
                    <t.icon className="h-6 w-6 text-brand-orange" />
                  </div>
                  <h3 className="mt-5 font-heading font-bold text-navy text-xl">{t.name}</h3>
                  <p className="mt-2 text-ink leading-relaxed">{t.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <div className="mt-10">
            <Link to={routes.treatments} data-testid="explore-treatments" className="inline-flex items-center gap-2 font-bold text-navy hover:text-brand-orange transition-colors group">
              Explore Treatments <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      {/* WHY CHOOSE */}
      <section className="relative overflow-hidden bg-navy py-20 lg:py-28" data-testid="why-choose-section">
        <div className="absolute inset-0 dotted-grid opacity-20" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading
            light
            eyebrow="Why Choose Dr. Satyam"
            title="Care That Puts You First"
            sub="A trusted, patient-focused approach designed around understanding, comfort and lasting results."
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {whyChoose.map((w, i) => (
              <Reveal key={w.title} delay={i * 60}>
                <div className="h-full rounded-2xl bg-white/5 border border-white/10 p-7 hover:bg-white/[0.08] transition-colors">
                  <w.icon className="h-8 w-8 text-brand-orange" />
                  <h3 className="mt-4 font-heading font-bold text-white text-lg">{w.title}</h3>
                  <p className="mt-2 text-white/70 leading-relaxed text-sm">{w.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="bg-white py-20 lg:py-28" data-testid="process-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading
            center
            eyebrow="Treatment Process"
            title="Your Journey to Better Movement"
            sub="A simple, transparent process — so you always know what to expect."
          />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {process.map((p, i) => (
              <Reveal key={p.step} delay={i * 80}>
                <div className="relative h-full bg-soft rounded-2xl p-8">
                  <span className="font-heading font-extrabold text-5xl text-brand-orange/25">{p.step}</span>
                  <h3 className="mt-3 font-heading font-bold text-navy text-lg">{p.title}</h3>
                  <p className="mt-2 text-sm text-ink leading-relaxed">{p.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* VIDEO TESTIMONIALS */}
      <section className="relative overflow-hidden bg-navy py-20 lg:py-28" data-testid="video-testimonials-section">
        <div className="absolute inset-0 dotted-grid opacity-20" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <VideoTestimonials
            dark
            sub="Real patients sharing their recovery journeys — straight from our Instagram."
          />
        </div>
      </section>

      {/* TESTIMONIALS PREVIEW (Live Google Reviews) */}
      <section className="bg-soft py-20 lg:py-28" data-testid="testimonials-preview">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading
            center
            eyebrow="Patient Testimonials"
            title="What Our Patients Say"
            sub="Real, verified reviews from our patients on Google."
          />
          <Reveal>
            <GoogleReviews />
          </Reveal>
          <div className="text-center mt-10">
            <Link to={routes.testimonials} data-testid="view-all-testimonials" className="inline-flex items-center gap-2 font-bold text-navy hover:text-brand-orange transition-colors group">
              View All Testimonials <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      {/* RESOURCES PREVIEW */}
      <section className="bg-white py-20 lg:py-28" data-testid="resources-preview">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading
            center
            eyebrow="Resources"
            title="Learn, Watch &amp; Explore"
            sub="Educational content to help you understand chiropractic care, pain management and healthy movement."
          />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: BookOpen, title: "Blogs", desc: "Educational articles on posture, spine health and pain management.", img: images.spine },
              { icon: Video, title: "Videos", desc: "Doctor-led awareness clips and chiropractic information.", img: images.adjustment },
              { icon: Images, title: "Gallery", desc: "Clinic moments, professional highlights and awareness activities.", img: images.clinic },
            ].map((r, i) => (
              <Reveal key={r.title} delay={i * 80}>
                <Link to={routes.resources} data-testid={`resource-${r.title.toLowerCase()}`} className="group block overflow-hidden rounded-2xl bg-soft shadow-sm hover:shadow-md transition-shadow">
                  <div className="relative h-44 overflow-hidden">
                    <img src={r.img} alt={r.title} loading="lazy" className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    <div className="absolute inset-0 bg-navy/40" />
                    <div className="absolute bottom-4 left-4 grid h-11 w-11 place-items-center rounded-xl bg-brand-orange">
                      <r.icon className="h-5 w-5 text-white" />
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="font-heading font-bold text-navy text-xl">{r.title}</h3>
                    <p className="mt-2 text-sm text-ink leading-relaxed">{r.desc}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-soft py-20 lg:py-28" data-testid="faq-section">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <SectionHeading center eyebrow="FAQs" title="Frequently Asked Questions" />
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="bg-white rounded-xl px-5 mb-3 border-none shadow-sm">
                <AccordionTrigger data-testid={`faq-trigger-${i}`} className="text-left font-heading font-semibold text-navy hover:no-underline hover:text-brand-orange py-5">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-ink leading-relaxed pb-5">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <FinalCTA />
    </>
  );
}
